import { SimulationStatus, getSimulationStatus } from '@/enum/SimulationStatus'
import { AloBaseArray } from '@/models/alo/AloBaseArray'
import { AloDataRawSet } from '@/models/alo/AloDataRawSet'
import { AloDrift } from '@/models/alo/AloDrift'
import { AloGliderFileShort } from '@/models/alo/AloGliderFileShort'
import { AloLogbookItem } from '@/models/alo/AloLogbookItem'
import { AloMission, calculAllDomainsData, updateAloMissionExtent } from '@/models/alo/AloMission'
import { AloMissionPlan } from '@/models/alo/AloMissionPlan'
import { AloYo, IAloYoConstParam } from '@/models/alo/AloYo'
import { FilterMission } from '@/models/filter/FilterMission'
import { IAd2cpConfig } from '@/models/iris/ad2cp/IAd2cpConfig'
import { IAisConfig } from '@/models/iris/ais/IAisConfig'
import { ICoriolisConfig } from '@/models/iris/coriolis/ICoriolisConfig'
import { GliderModuleApiId, IGliderRuntime } from '@/models/iris/gliderState/IGliderRuntime'
import {
  deriveDevicesFromFlatRow,
  deriveNavTemplateFromFlatRow,
  flattenNmeaRow,
} from '@/models/iris/gliderState/gliderRuntimeAdapter'
import { IQuickControlConfig } from '@/models/iris/quickControlConfig/IQuickControlConfig'
import { IrisConfigContainer } from '@/models/iris/IrisConfigContainer'
import { IMissionPlan } from '@/models/iris/missionPlan/IMissionPlan'
import { IMissionPlanConfig } from '@/models/iris/missionPlanConfig/IMissionPlanConfig'
import { ISendMailConfig } from '@/models/iris/sendMail/ISendMailConfig'
import { ISmartMission } from '@/models/iris/smartMission/ISmartMission'
import { getDefaultSmartResult } from '@/models/iris/smartMission/ISmartResult'
import { ConsoleRow } from '@/models/ui/ConsoleRow'
import { MISSIONS_DETAIL_TABS } from '@/models/ui/MissionTabs'
import { MissionUi } from '@/models/ui/MissionUi'
import { MissionUiState } from '@/models/ui/MissionUiState'
import { UiTab } from '@/models/ui/UiTab'
import { UiTabs } from '@/models/ui/UiTabs'
import { WobGliderFile } from '@/models/wob/WobGliderFile'
import { wobGliderNmea } from '@/models/wob/WobGliderNmea'
import { WobSimProgress } from '@/models/wob/WobSimProgress'
import deepEqual from 'deep-equal'
import Vue from 'vue'
import { MutationTree } from 'vuex/types'
import * as types from './mutation-types'
import { IMissionState } from './state'
//import {extractDistinctNumberProp} from '@/utils/groupBy'

/**
 * Les listes de mails IRIS (SendMail) reviennent en `string` quand il n'y a
 * qu'un seul élément (et `''` quand la liste est vide). Sans normalisation, un
 * `v-for` itère les caractères (1 chip par lettre) et un `.push` plante. On
 * force `string[]` à la source (mutations de chargement/save).
 */
function ensureMailArray(v: unknown): string[] {
  if (Array.isArray(v)) return v as string[]
  if (v == null || v === '') return []
  return [v as string]
}
function normalizeSendMailArrays(cfg: IrisConfigContainer<ISendMailConfig>): void {
  const m = (cfg?.mod_settings as any)?.SendMail_mailling
  if (!m) return
  m.mails_glider_alr = ensureMailArray(m.mails_glider_alr)
  m.mails_iris_alr = ensureMailArray(m.mails_iris_alr)
  m.mails_surfacing = ensureMailArray(m.mails_surfacing)
  m.mails_detection = ensureMailArray(m.mails_detection)
}

/*
  Need to use export default({}) rather than export default {}
  ref links: https://github.com/TypeStrong/ts-loader/issues/588
*/
function deepFreeze(object: any, freeze = true) {
  // Retrieve the property names defined on object
  const propNames = Reflect.ownKeys(object)

  // Freeze properties before freezing self
  for (const name of propNames) {
    const value = object[name]

    if ((value && typeof value === 'object') || typeof value === 'function') {
      //pour ne pas bloquer le tableau de nmea
      deepFreeze(value, name != 'dataSet')
    }
  }

  //on ne freeze pas les mission appartenant à des flottes
  return freeze && !object?.fleId ? Object.freeze(object) : object
}

const mutations: MutationTree<IMissionState> = {
  //Gestion des recherches de missions
  [types.MISSION_REQUEST]: (state, ongoing: boolean | null = null) => {
    if (ongoing != null && ongoing) state.missionsOngoingLoading = true
    if (ongoing != null && !ongoing) state.missionsPassedLoading = true
    state.status = `loading missions${ongoing == null ? '' : ongoing ? ' en cours' : ' passées'}`
  },
  [types.MISSION_SUCCESS]: (state, items: AloMission[]) => {
    state.missionsOngoingLoading = false
    state.status = 'success loading missions'
    //on remplace les missions de la reponse par les missions du store déja présente
    state.missions.forEach(m => {
      const idx = items.findIndex(rm => rm.misId == m.misId && m._detailLoaded)
      if (idx >= 0) {
        items.splice(idx, 1, m)
      }
    })
    state.missions = items
  },
  [types.MISSION_PASSED_SUCCESS]: (state, { items, gliIds }: { items: AloMission[]; gliIds: Array<number> }) => {
    state.missionsPassedLoading = false
    state.missionsPassedLoaded = [...state.missionsPassedLoaded, ...gliIds]
    state.status = 'success loading missions not ongoing'
    deepFreeze(items, false)
    //on remplace les missions de la reponse par les missions du store avec detail déja présente
    state.missions.forEach(m => {
      const idx = items.findIndex(rm => rm.misId == m.misId && m._detailLoaded)
      if (idx >= 0) {
        items.splice(idx, 1, m)
      }
    })
    state.missions = [
      ...state.missions.filter(m => m.misActifOn),
      ...state.missions.filter(m => !m.misActifOn && items.every(it => it.misId != m.misId)),
      ...items,
    ]
  },
  [types.MISSION_PASSED_PRELOAD_SUCCESS]: (state, items: AloMission[]) => {
    state.missionsPassedLoading = false
    state.status = 'success pre loading missions not ongoing'
    state.missions = [...state.missions.filter(m => m.misActifOn), ...items]
  },
  [types.MISSION_ERROR]: state => {
    state.missionsPassedLoading = false
    state.missionsOngoingLoading = false
    state.status = 'error loading missions'
    //state.missions = []
  },
  //gestion de la recuperation d'une mission
  [types.MISSION_LOADING_REQUEST]: (state, id: number) => {
    state.status = `loading mission ${id}`
  },
  [types.MISSION_DELETE]: (state, id: number) => {
    const index = state.missions.findIndex(m => m.misId == id)
    state.missions.splice(index == -1 ? state.missions.length : index, index == -1 ? 0 : 1)
    const detailIndex = state.detailMissions.findIndex(it => it.mission.misId == id)
    state.detailMissions.splice(
      detailIndex == -1 ? state.detailMissions.length : detailIndex,
      detailIndex == -1 ? 0 : 1,
    )
  },
  [types.MISSION_LOADING_SUCCESS]: (state, mission: AloMission) => {
    state.status = `success loading mission ${mission.misId}`
    //TODO gérer les champs calculées sur mission

    //Mise à jour des références mission
    //1, on met à jour la mission dans la liste des missions
    const index = state.missions.findIndex(m => m.misId == mission.misId)
    // const oldMission = index > -1 ? state.missions[index] : null
    // if (oldMission) {
    //   //on conserve les données non rechargées
    //   mission.dataScience = oldMission.dataScience
    //   mission._dataScienceLoaded = oldMission._dataScienceLoaded
    //   mission._dataScienceLoading = oldMission._dataScienceLoading
    //   mission.dataNavigation = oldMission.dataNavigation
    //   mission._dataNavigationLoaded = oldMission._dataNavigationLoaded
    //   mission._dataNavigationLoading = oldMission._dataNavigationLoading
    //   mission.logBookItems = oldMission.logBookItems
    //   mission._logBookLoaded = oldMission._logBookLoaded
    //   mission.dataNmea = oldMission.dataNmea
    //   mission._detailNmeaLoaded = oldMission._detailNmeaLoaded
    // }
    state.missions.splice(index == -1 ? state.missions.length : index, index == -1 ? 0 : 1, mission)

    //2, on met à jour la mission dans la liste des selected Mission si elle existe
    /*const selectedMissionsIndex = state.selectedMissions.findIndex(m => m.misId == mission.misId)
    if (selectedMissionsIndex > -1) {
      state.selectedMissions.splice(selectedMissionsIndex, 1, mission)
    }*/

    //3, on met à jour la mission si elle existe dans le tableau des details Mission
    const detailIndex = state.detailMissions.findIndex(it => it.mission.misId == mission.misId)
    if (detailIndex > -1) {
      //on met a jour les donnees de la mission
      Vue.set(state.detailMissions[detailIndex], 'mission', mission)
    }
  },
  [types.MISSION_LOADING_ERROR]: (state, id: number) => {
    state.status = `error loading mission ${id}`
  },
  //gestion de la recuperation des variables sciences d'une mission
  [types.MISSION_LOADING_SCIVARIABLE_REQUEST]: (state, id: number) => {
    state.status = `loading sci variables mission ${id}`
  },
  [types.MISSION_LOADING_SCIVARIABLE_SUCCESS]: (state, mission: AloMission) => {
    state.status = `success loading sci variables mission ${mission.misId}`
    const index = state.missions.findIndex(m => m.misId == mission.misId)
    //si mission trouvé on met à jour les sciPlotVariables
    if (index > -1) state.missions[index].sciPlotVariables = mission.sciPlotVariables
  },
  [types.MISSION_LOADING_SCIVARIABLE_ERROR]: (state, id: number) => {
    state.status = `error loading sci variables mission ${id}`
  },
  [types.MISSION_DATA_SCIENCE_REQUEST]: (state, id: number) => {
    state.status = `loading science data for mission ${id}`
    const index = state.missions.findIndex(m => m.misId == id)
    if (index > -1) {
      const mission = state.missions[index]
      mission._dataScienceLoading = true
    }
  },
  [types.MISSION_DATA_SCIENCE_SUCCESS]: (
    state,
    { id, datas, targetYosCycles }: { id: number; datas: any; targetYosCycles: Array<number> | null },
  ) => {
    state.status = `success loading science data for mission ${id}`

    //Mise à jour des données sciences de la mission
    const index = state.missions.findIndex(m => m.misId == id)
    if (index > -1) {
      const mission = state.missions[index]

      //Si la mission a deja des données sciences on est dans le cas d'une mise à jour ou d'un ajout
      if (targetYosCycles && mission.dataScience.length > 0) {
        //const yosCycles = extractDistinctNumberProp(datas,d => d.yosCycle)
        datas = [...mission.dataScience.filter(d => targetYosCycles.every(y => y != d.yosCycle)), ...datas].sort(
          (a, b) => a.PLD_REALTIMECLOCK.getTime() - b.PLD_REALTIMECLOCK.getTime(),
        )
      }

      calculAllDomainsData(
        mission.sciPlotVariables,
        mission.xSciScatterVariable,
        mission.domains,
        mission.domainsAutoScale,
        mission.domainsColor,
        datas,
      )
      mission.dataScience = datas
      mission._dataScienceLoaded = true
      mission._dataScienceLoading = false
    }
  },
  [types.MISSION_DATA_SCIENCE_ERROR]: (state, id: number) => {
    state.status = `error loading science data for mission ${id}`
    const index = state.missions.findIndex(m => m.misId == id)
    if (index > -1) {
      const mission = state.missions[index]
      mission._dataScienceLoading = false
    }
  },
  [types.MISSION_DATA_NMEA_REQUEST]: (state, { typeNmea, id }: { typeNmea: string; id: number }) => {
    state.status = `loading nmea ${typeNmea} data for mission ${id}`
  },
  [types.MISSION_DATA_NMEA_SUCCESS]: (
    state,
    { typeNmea, id, data }: { typeNmea: string; id: number; data: AloDataRawSet },
  ) => {
    state.status = `success loading  nmea ${typeNmea} data for mission ${id}`

    //Mise à jour des données nmea de la mission
    const index = state.missions.findIndex(m => m.misId == id)
    if (index > -1) {
      const mission = state.missions[index]
      deepFreeze(data)
      Vue.set(mission.dataNmea, typeNmea, data)
      Vue.set(mission._detailNmeaLoaded, typeNmea, true)

      //s'assure que le type nmea est bien présent
      if (mission.nmeaTypeArray.indexOf(typeNmea) == -1) mission.nmeaTypeArray.push(typeNmea)
      mission._refresh = !mission._refresh
    }
  },
  [types.MISSION_DATA_NMEA_ERROR]: (state, { typeNmea, id }: { typeNmea: string; id: number }) => {
    state.status = `error loading  nmea ${typeNmea} data for mission ${id}`
  },
  [types.MISSION_DRIFT_REQUEST]: (state, id: number) => {
    state.status = `loading drift data for mission ${id}`
  },
  [types.MISSION_DRIFT_SUCCESS]: (state, { id, yos }: { id: number; yos: AloBaseArray<AloYo> }) => {
    state.status = `success loading drift data for mission ${id}`

    //Mise à jour des données des yos de la mission
    const index = state.missions.findIndex(m => m.misId == id)
    if (index > -1) {
      const mission = state.missions[index]
      //Mise à jour des données des yos avec les drifts
      mission.yoArray = yos.items
        .map(yo => {
          Object.freeze(yo)
          return yo
        })
        .sort(function (a, b) {
          return a.yoGpsDatTime.getTime() - b.yoGpsDatTime.getTime()
        })

      mission._driftLoaded = true
    }
  },
  [types.MISSION_DRIFT_ERROR]: (state, id: number) => {
    state.status = `error loading science data for mission ${id}`
  },
  [types.MISSION_IRIS_GLIDER_STATE_REQUEST]: (state, id: number) => {
    state.status = `loading glider state for mission ${id}`
  },
  [types.MISSION_IRIS_GLIDER_STATE_SUCCESS]: (
    state,
    { id, gliderRuntime }: { id: number; gliderRuntime: IGliderRuntime },
  ) => {
    state.status = `success loading glider state for mission ${id}`

    //Mise à jour du runtime glider de la mission
    const index = state.missions.findIndex(m => m.misId == id)
    if (index > -1) {
      const mission = state.missions[index]
      mission.gliderRuntime = gliderRuntime
    }
  },
  [types.MISSION_IRIS_GLIDER_STATE_ERROR]: (state, id: number) => {
    state.status = `error loading glider state for mission ${id}`
  },
  /**
   * Patch un seul module enable dans gliderRuntime.modules. Cible toutes les
   * missions du gliderId (un glider peut avoir plusieurs missions historiques
   * ouvertes — l'état "enabled" est glider-level). Filtre défensif : missions
   * sans runtime initialisé sont ignorées (pas de cold-create).
   */
  [types.GLIDER_RUNTIME_PATCH_MODULE_ENABLE]: (
    state,
    { gliId, moduleKey, enable }: { gliId: number; moduleKey: GliderModuleApiId; enable: boolean },
  ) => {
    state.missions.forEach(m => {
      if (m.gliId === gliId && m.gliderRuntime) {
        Vue.set(m.gliderRuntime.modules, moduleKey, enable)
        Vue.set(m.gliderRuntime, 'lastUpdate', new Date())
      }
    })
  },
  /**
   * Patch alarmCode du runtime glider. Cible toutes les missions du gliderId
   * (état alarme = glider-level, comme PATCH_MODULE_ENABLE). Filtre défensif :
   * missions sans runtime initialisé ignorées (pas de cold-create).
   */
  [types.GLIDER_RUNTIME_PATCH_ALARM]: (state, { gliId, alarmCode }: { gliId: number; alarmCode: number }) => {
    state.missions.forEach(m => {
      if (m.gliId === gliId && m.gliderRuntime) {
        Vue.set(m.gliderRuntime, 'alarmCode', alarmCode)
        Vue.set(m.gliderRuntime, 'lastUpdate', new Date())
      }
    })
  },
  [types.MISSION_DATA_NAVIGATION_REQUEST]: (state, id: number) => {
    state.status = `loading navigation data for mission ${id}`

    const index = state.missions.findIndex(m => m.misId == id)
    if (index > -1) {
      const mission = state.missions[index]
      mission._dataNavigationLoading = true
    }
  },
  [types.MISSION_DATA_NAVIGATION_SUCCESS]: (
    state,
    { id, datas, targetYosCycles }: { id: number; datas: any; targetYosCycles: Array<number> | null },
  ) => {
    state.status = `success loading navigation data for mission ${id}`

    //Mise à jour des données sciences de la mission
    const index = state.missions.findIndex(m => m.misId == id)

    if (index > -1) {
      const mission = state.missions[index]

      //Si la mission a deja des données sciences on est dans le cas d'une mise à jour ou d'un ajout
      if (targetYosCycles && mission.dataNavigation.length > 0) {
        //const yosCycles = extractDistinctNumberProp(datas,d => d.yosCycle)
        datas = [...mission.dataNavigation.filter(d => targetYosCycles.every(y => y != d.yosCycle)), ...datas].sort(
          (a, b) => a.datNav.getTime() - b.datNav.getTime(),
        )
      }

      calculAllDomainsData(
        mission.navPlotVariables,
        mission.xNavScatterVariable,
        mission.domains,
        mission.domainsAutoScale,
        mission.domainsColor,
        datas,
      )
      mission.dataNavigation = datas
      mission._dataNavigationLoaded = true
      mission._dataNavigationLoading = false
    }
  },
  [types.MISSION_DATA_NAVIGATION_ERROR]: (state, id: number) => {
    state.status = `error loading navigation data for mission ${id}`
    //Mise à jour des données sciences de la mission
    const index = state.missions.findIndex(m => m.misId == id)

    if (index > -1) {
      const mission = state.missions[index]
      mission._dataNavigationLoading = false
    }
  },
  //deselectione la mission
  [types.MISSION_UNSELECT]: state => {},
  [types.MISSION_SELECT_DETAIL_SUCCESS]: (state, mission: AloMission) => {
    state.status = `success loading mission ${mission.misId}`
    const newIdx = state.detailMissions.findIndex(it => it.mission.misId == mission.misId)
    if (newIdx == -1) {
      //on cree un nouvel onglet map
      const leftCmp = MISSIONS_DETAIL_TABS.find(it => it.cmp == 'MissionDetailMap')
      if (leftCmp) {
        const newLeft = new UiTab(leftCmp)
        leftCmp.id++ //augmente l'id pour la prochaine creation

        /*const rightCmp = MISSIONS_DETAIL_TABS.find(it => it.cmp == "MissionDetailScienceGraphicsList")      
        const newRight = new UiTab(rightCmp)
        rightCmp.id++ //augmente l'id pour la prochaine creation     */

        state.detailMissions.push(
          new MissionUi({
            mission,
            tabsleft: new UiTabs({
              tabs: [newLeft],
              selected: `${newLeft.cmp}-${newLeft.id}`,
            }),
            tabsright: new UiTabs({
              selected: 'MissionMenu-0',
            }),
          }),
        )
      }
    }
  },
  [types.MISSION_SELECT_DETAIL_ERROR]: (state, id: number) => {
    state.status = `error loading mission ${id}`
  },
  //deselectione la mission
  [types.MISSION_UNSELECT_DETAIL]: (state, id: number) => {
    const newIdx = state.detailMissions.findIndex(it => it.mission.misId == id)
    if (newIdx > -1) {
      //reset des données sciences et navigation...
      const mission = state.detailMissions[newIdx].mission
      //si des images on été en cache on les vide
      if ((window as any)[`imgcache_${mission.misId}`]) {
        ;(window as any)[`imgcache_${mission.misId}`] = {}
      }
      if (mission._dataScienceLoaded) {
        mission._dataScienceLoaded = false
        //vide les données sciences
        mission.dataScience.length = 0
      }
      if (mission._dataNavigationLoaded) {
        mission._dataNavigationLoaded = false
        //vide les données de navigation
        mission.dataNavigation.length = 0
      }
      if (mission._downloadFileStatusLoaded) {
        mission._downloadFileStatusLoaded = false
        //vide les données de navigation
        mission.downloadFileStatus!.length = 0
      }
      if (state.currentDetailMission.mission.misId == id) state.currentDetailMission = new MissionUi()
      state.detailMissions.splice(newIdx, 1)
    }
  },
  [types.MISSION_SELECT_DETAIL]: (state, id: number) => {
    const missionUi = state.detailMissions.find(it => it.mission.misId == id)
    state.currentDetailMission = missionUi || new MissionUi()
  },
  [types.MISSION_NAVTO_DETAIL]: (
    state,
    { cmp, isleft, id, label }: { cmp: string; isleft: boolean; id: number; label: string },
  ) => {
    const tabsMission = isleft ? state.currentDetailMission.tabsleft : state.currentDetailMission.tabsright
    const newIdx = tabsMission.tabs.findIndex(it => it.cmp == cmp && it.id == id)
    if (newIdx == -1) {
      const cmpfound = MISSIONS_DETAIL_TABS.find(it => it.cmp == cmp)
      if (cmpfound) {
        const newComp = new UiTab({ ...cmpfound, label: label || cmpfound.label })
        cmpfound.id++ //augmente l'id pour la prochaine creation
        tabsMission.tabs.push(newComp)
        tabsMission.selected = `${cmp}-${newComp.id}`
        return
      }
    }

    tabsMission.selected = `${cmp}-${id}`
  },
  [types.MISSION_SET_LEFTPANEL_SIZE]: (state, size: string) => {
    state.currentDetailMission.leftPanelWidth = size
  },
  [types.MISSION_UNSELECT_TAB]: (state, tab: UiTab) => {
    tab.tryClose = !tab.tryClose
  },
  [types.MISSION_CLOSE_TAB]: (state, { cmp, isleft, id }: { cmp: string; isleft: boolean; id: number }) => {
    const tabsMission = isleft ? state.currentDetailMission.tabsleft : state.currentDetailMission.tabsright
    const newIdx = tabsMission.tabs.findIndex(it => it.cmp == cmp && it.id == id)
    if (newIdx > -1) {
      tabsMission.tabs.splice(newIdx, 1)
    }
    if (tabsMission.selected == `${cmp}-${id}`)
      tabsMission.selected =
        tabsMission.tabs.length > 0 ? `${tabsMission.tabs[0].cmp}-${tabsMission.tabs[0].id}` : 'MissionMenu-0'
  },
  [types.MISSION_RENAME_DETAIL]: (
    state,
    { cmp, isleft, id, label }: { cmp: string; isleft: boolean; id: number; label: string },
  ) => {
    const tabsMission = isleft ? state.currentDetailMission.tabsleft : state.currentDetailMission.tabsright
    const newIdx = tabsMission.tabs.findIndex(it => it.cmp == cmp && it.id == id)
    if (newIdx > -1) {
      tabsMission.tabs[newIdx].label = label
    }
  },
  [types.MISSION_MOVE_TAB]: (state, { cmp, isleft, id }: { cmp: string; isleft: boolean; id: number }) => {
    const tabsMission = isleft ? state.currentDetailMission.tabsleft : state.currentDetailMission.tabsright
    const newIdx = tabsMission.tabs.findIndex(it => it.cmp == cmp && it.id == id)
    if (newIdx > -1) {
      const tagetTabsMission = isleft ? state.currentDetailMission.tabsright : state.currentDetailMission.tabsleft
      const selectedTab = tabsMission.tabs.splice(newIdx, 1)[0]
      if (tabsMission.selected == `${cmp}-${id}`)
        tabsMission.selected =
          tabsMission.tabs.length > 0 ? `${tabsMission.tabs[0].cmp}-${tabsMission.tabs[0].id}` : 'MissionMenu-0'
      //on attend le next tick pour reconstruir le composant pour etre certain que les listener on bien été clear avant la reconstruction
      Vue.nextTick(() => {
        tagetTabsMission.tabs.push(selectedTab)
        tagetTabsMission.selected = `${cmp}-${id}`
      })
    }
  },
  [types.MISSION_SAVE_IMAGE_REQUEST]: (state, name: string) => {
    state.status = `saving image mission ${name}`
  },
  [types.MISSION_SAVE_IMAGE_SUCCESS]: (state, name: string) => {
    state.status = `success saving image mission ${name}`
  },
  [types.MISSION_SAVE_IMAGE_ERROR]: (state, name: string) => {
    state.status = `error saving image mission ${name}`
  },
  [types.MISSION_SET_GLIDER_FILTER]: (state, ids: number[]) => {
    state.filterMission.gliderIds = ids
  },
  [types.MISSION_SET_ENSEIGNE_FILTER]: (state, ids: number[]) => {
    state.filterMission.hasSetEnsids = true
    state.filterMission.ensIds = ids
  },
  [types.MISSION_SET_ALARM_FILTER]: (state, ids: string[]) => {
    state.filterMission.alarmIds = ids
  },
  [types.MISSION_SET_ONGOING_FILTER]: (state, ongoing: boolean) => {
    state.filterMission.onGoing = ongoing
  },
  [types.MISSION_SET_SIMULATED_FILTER]: (state, simulated: boolean) => {
    state.filterMission.simulated = simulated
  },
  [types.MISSION_RESET_FILTER]: (state, ongoing: boolean) => {
    state.filterMission = new FilterMission()
  },
  [types.MISSION_ADD_CONSOLE_ROW]: (state, { consoleMsgs, row }: { consoleMsgs: ConsoleRow[]; row: ConsoleRow }) => {
    consoleMsgs.push(row)
  },
  [types.MISSION_CLEAR_CONSOLE]: (state, missionState: MissionUiState) => {
    missionState.consoleMsgs = []
  },
  [types.MISSION_UPDATE]: (state, { mission, wob }: { mission: AloMission; wob: any }) => {
    if (wob.alarmStatus) mission.alarmStatusLast = wob.alarmStatus.split(', ')
    if (wob.gliderWaitingMode) {
      mission.gliderWaitingMode = wob.gliderWaitingMode
    }
    if (wob.aisStatus) {
      mission.aisStatus = wob.aisStatus
    }
    if ((wob?.isTestMission ?? null) != null) {
      mission.isTestMission = wob.isTestMission
    }
    if ((wob?.misActifOn ?? null) != null) {
      mission.misActifOn = wob.misActifOn
    }
    if ((wob?.isGeoHidden ?? null) != null) {
      mission.isGeoHidden = wob.isGeoHidden
    }
    //Mise à jour du yo si cycle présent
    if (wob.gliCycle != undefined) {
      let yo = mission.yoArray.find(y => y.yosCycle == wob.gliCycle)

      if (yo === undefined) {
        //si le yo n'existe pas et que l'on a pas les infos de lat long et gps on ne le créé pas encore
        if (wob.gpsLonDms !== undefined && wob.gpsLatDms !== undefined && wob.gpsDatTime !== undefined) {
          yo = new AloYo({ yosCycle: wob.gliCycle } as IAloYoConstParam)
        }
      } else {
        //on recree le yo pour enlever le freeze et pouvoir reassigner les valeurs
        yo = new AloYo({
          ...yo,
          yoGpsDatTime: yo?.yoGpsDatTime?.toString() == 'Invalid Date' ? '' : yo?.yoGpsDatTime?.toISOString(),
          driftArray: yo.driftArray.map(d => {
            return {
              ...d,
              yoGpsDatTime: d?.yoGpsDatTime?.toString() == 'Invalid Date' ? '' : d?.yoGpsDatTime?.toISOString(),
            }
          }),
        })
      }
      if (yo) {
        //const yoToUpdate = mission.yoArray
        //Mise à jour du tableau de Drift
        if (wob.gpsLonDms !== undefined && wob.gpsLatDms !== undefined && wob.gpsDatTime !== undefined) {
          yo.driftArray.push(
            new AloDrift({
              yoGpsDatTime: wob.gpsDatTime,
              yoGpsLat: wob.gpsLatDms,
              yoGpsLon: wob.gpsLonDms,
            }),
          )
        }

        if (wob.gpsLonDms !== undefined) yo.yoGpsLon = wob.gpsLonDms
        if (wob.gpsLatDms !== undefined) yo.yoGpsLat = wob.gpsLatDms
        if (wob.gpsDatTime !== undefined) yo.yoGpsDatTime = new Date(wob.gpsDatTime)
        if (wob.batteries !== undefined) yo.batteries = wob.batteries

        if (wob.heading !== undefined) {
          yo.heading = wob.heading
        }
        if (wob.zb !== undefined) yo.zb = wob.zb
        if (wob.zt !== undefined) yo.zt = wob.zt
        if (wob.alt !== undefined) yo.alt = wob.alt
        if (wob.pu !== undefined) yo.pu = wob.pu
        if (wob.pd !== undefined) yo.pd = wob.pd
        if (wob.bu !== undefined) yo.bu = wob.bu
        if (wob.bd !== undefined) yo.bd = wob.bd
        if (wob.rate !== undefined) yo.rate = wob.rate
        Object.freeze(yo)
        //on injecte le yo dans les yoArray
        const indexToDelete = mission.yoArray.findIndex(m => m.yosCycle == wob.gliCycle)
        //si le yo existe on le supprime
        if (indexToDelete != -1) {
          mission.yoArray.splice(indexToDelete, 1)
        }
        //on recherche l'emplacement d'injection
        const indexToInject = mission.yoArray.findIndex(m => m.yoGpsDatTime >= yo!.yoGpsDatTime)
        mission.yoArray.splice(indexToInject == -1 ? mission.yoArray.length : indexToInject, 0, yo)

        //injecte les datas des yos manquant à la fin si multiyos
        if (mission.yoArray.length > 2) {
          let startCycle = mission.yoArray[mission.yoArray.length - 2].yosCycle! + 1
          const endCycle = mission.yoArray[mission.yoArray.length - 1].yosCycle!
          while (startCycle < endCycle) {
            mission.yoArray.splice(
              mission.yoArray.length - 1,
              0,
              new AloYo({ yosCycle: startCycle } as IAloYoConstParam),
            )
            startCycle++
          }
        }

        //mise à jour de l'extent
        updateAloMissionExtent(mission)
      }
    }
  },
  [types.MISSION_UPDATE_NMEA]: (state, { mission, wob }: { mission: AloMission; wob: wobGliderNmea }) => {
    //@ts-ignore
    Object.keys(wob.nmeaData.dataSet).forEach(key => {
      const yoCycle = Number(key)
      if (!isNaN(yoCycle)) {
        Vue.set(mission.dataNmea[wob.nmeaData.libelle].dataSet, yoCycle, wob.nmeaData.dataSet[yoCycle])
        mission._refresh = !mission._refresh
      }
    })

    // Enrichissement gliderRuntime depuis les trames pertinentes (SEADEV → devices,
    // SEANAV → navTemplate). Permet aux composants qui lisent gliderRuntime de
    // recevoir le temps réel sans re-fetcher IRIS gliderState.
    // ⚠ La dataSet[yoCycle] est une ligne `Array<any>` indexée selon `columns[].pos`.
    if (mission.gliderRuntime) {
      const libelle = (wob.nmeaData.libelle ?? '').toLowerCase()
      const columns = wob.nmeaData.columns ?? []
      const rows = Object.values(wob.nmeaData.dataSet ?? {}) as Array<Array<unknown>>
      const lastRow = rows.length ? rows[rows.length - 1] : null
      if (lastRow && (libelle === 'seadev' || libelle === 'seanav')) {
        const flat = flattenNmeaRow(columns, lastRow)
        if (libelle === 'seadev') {
          Vue.set(mission.gliderRuntime, 'devices', deriveDevicesFromFlatRow(flat as never))
        } else {
          Vue.set(mission.gliderRuntime, 'navTemplate', deriveNavTemplateFromFlatRow(flat as never))
        }
        Vue.set(mission.gliderRuntime, 'lastUpdate', new Date())
      }
    }
  },
  [types.MISSION_UPDATE_DOWNLOAD_FILE_STATUS]: (
    state,
    { mission, wob }: { mission: AloMission; wob: WobGliderFile },
  ) => {
    //Si on a bien un targetYosCycles
    if (wob.targetYosCycles) {
      wob.targetYosCycles.forEach(cycle => {
        if (cycle != 0) {
          const glFile = mission.downloadFileStatus!.find(
            f => f.targetYosCycle! == cycle && f.gliderFileType == wob.gliderFileType,
          )
          if (glFile) {
            glFile.dataProgression = wob.dataProgression
            glFile.isDownloadable = glFile.isDownloadable || wob.isDownloadable
            glFile.isRunning = glFile.isRunning || wob.isRunning
          } else {
            const maxCycle = Math.max(...mission.downloadFileStatus!.map(f => f.targetYosCycle!))
            //on injecte les wob intermediaires
            for (var i = maxCycle + 1; i < cycle; i++)
              mission.downloadFileStatus!.push(
                new AloGliderFileShort({
                  gliderFileType: wob.gliderFileType,
                  isDownloadable: false,
                  dataProgression: null,
                  targetYosCycle: i,
                  isRunning: false,
                }),
              )
            mission.downloadFileStatus!.push(
              new AloGliderFileShort({
                ...wob,
                isDownloadable: wob.isDownloadable,
                targetYosCycle: cycle,
                isRunning: wob.isRunning,
              }),
            )
          }
        }
      })
    }
    //on regarde si un traitement est terminé sur les yos
    /*if() {

    }*/
  },
  [types.MISSION_DOWNLOAD_FILE_STATUS_REQUEST]: (state, id: number) => {
    state.status = `loading file status ${id}`
  },
  [types.MISSION_DOWNLOAD_FILE_STATUS_SUCCESS]: (state, { id, datas }: { id: number; datas: AloGliderFileShort[] }) => {
    state.status = `success loading file status  ${id}`

    //Mise à jour des données file status
    const index = state.missions.findIndex(m => m.misId == id)
    if (index > -1) {
      const mission = state.missions[index]
      mission.downloadFileStatus = datas
      mission._downloadFileStatusLoaded = true
    }
  },
  [types.MISSION_DOWNLOAD_FILE_STATUS_ERROR]: (state, id: number) => {
    state.status = `error loading file status ${id}`
  },
  [types.MISSION_LOGBOOK_REQUEST]: (state, id) => {
    state.status = `loading logbook from mission ${id}`
  },
  [types.MISSION_LOGBOOK_SUCCESS]: (state, { id, datas }: { id: number; datas: AloLogbookItem[] }) => {
    state.status = `success loading logbook from mission  ${id}`

    //Mise à jour des données file status
    const index = state.missions.findIndex(m => m.misId == id)

    if (index > -1) {
      const mission = state.missions[index]
      if (mission) {
        mission.logBookItems = datas
        mission._logBookLoaded = true
      }
    }
  },
  [types.MISSION_LOGBOOK_ERROR]: (state, id: number) => {
    state.status = `error loading logbook from mission ${id}`
  },
  [types.MISSION_LOGBOOK_ITEM_REQUEST]: (state, { misId, logBookId }: { misId: number; logBookId: number }) => {
    state.status = `loading logbookitem ${logBookId} from mission ${misId}`
  },
  [types.MISSION_LOGBOOK_ITEM_SUCCESS]: (state, { misId, data }: { misId: number; data: AloLogbookItem }) => {
    state.status = `success loading logbookitem ${data.lbkId} from mission  ${misId}`

    //Mise à jour des données file status
    const index = state.missions.findIndex(m => m.misId == misId)
    if (index > -1) {
      const mission = state.missions[index]
      //recherche si le logBook contient l'item et le remplace si necessaire
      //on met à jour le logbookitem dans le logbook
      const indexLog = mission.logBookItems.findIndex(m => m.lbkId == data.lbkId)
      mission.logBookItems.splice(indexLog == -1 ? mission.logBookItems.length : indexLog, indexLog == -1 ? 0 : 1, data)
    }
  },
  [types.MISSION_LOGBOOK_ITEM_ERROR]: (state, { misId, logBookId }: { misId: number; logBookId: number }) => {
    state.status = `error loading logbookitem ${logBookId} from mission ${misId}`
  },
  [types.MISSION_LOGBOOK_SEND_MESSAGE_REQUEST]: (state, misId: number) => {
    state.status = `send message to logbook from mission ${misId}`
  },
  [types.MISSION_LOGBOOK_SEND_MESSAGE_ERROR]: (state, misId: number) => {
    state.status = `error send message to logbook from mission ${misId}`
  },
  [types.MISSION_LOGBOOK_UPDATE_MESSAGE_REQUEST]: (state, { misId, lbkId }: { misId: number; lbkId: number }) => {
    state.status = `update message ${lbkId} to logbook from mission ${misId}`
  },
  [types.MISSION_LOGBOOK_UPDATE_MESSAGE_ERROR]: (state, { misId, lbkId }: { misId: number; lbkId: number }) => {
    state.status = `update message ${lbkId} to logbook from mission ${misId}`
  },
  [types.MISSION_IRIS_SEND_MAIL_REQUEST]: state => {
    state.status = 'loading send mail config'
    state.sendMailLoading = true
  },
  [types.MISSION_IRIS_SEND_MAIL_SUCCESS]: (
    state,
    { id, sendMailConfig }: { id: number; sendMailConfig: IrisConfigContainer<ISendMailConfig> },
  ) => {
    state.status = 'success loading send mail config'
    normalizeSendMailArrays(sendMailConfig)
    //console.log("mail equal",deepEqual(state.originalSendMailConfigs[id],state.modifiedSendMailConfigs[id]),state.originalSendMailConfigs[id],state.modifiedSendMailConfigs[id])
    //si les config sont identique on met à jour les deux
    if (deepEqual(state.originalSendMailConfigs[id], state.modifiedSendMailConfigs[id])) {
      const cloneConfig = new IrisConfigContainer(JSON.parse(JSON.stringify(sendMailConfig)))
      Vue.set(state.modifiedSendMailConfigs, id, cloneConfig)
    }
    Vue.set(state.originalSendMailConfigs, id, sendMailConfig)
    state.sendMailLoading = false
  },
  [types.MISSION_IRIS_SEND_MAIL_ERROR]: state => {
    state.status = 'error loading send mail config'
    state.sendMailLoading = false
  },
  [types.MISSION_UPDATE_MODIFIED_SEND_MAIL]: (
    state,
    { itemId, newProperties }: { itemId: number; newProperties: any },
  ) => {
    const modifiedConfig = state.modifiedSendMailConfigs[itemId].mod_settings
    if (newProperties.mod_settings?.hasOwnProperty('EnabledMOD'))
      modifiedConfig.EnabledMOD = newProperties.mod_settings.EnabledMOD
    if (newProperties.mod_settings?.SendMail_mailling?.hasOwnProperty('mails_glider_alr'))
      modifiedConfig.SendMail_mailling.mails_glider_alr = [
        ...newProperties.mod_settings.SendMail_mailling.mails_glider_alr,
      ]
    if (newProperties.mod_settings?.SendMail_mailling?.hasOwnProperty('mails_iris_alr'))
      modifiedConfig.SendMail_mailling.mails_iris_alr = [...newProperties.mod_settings.SendMail_mailling.mails_iris_alr]
    if (newProperties.mod_settings?.SendMail_mailling?.hasOwnProperty('mails_surfacing'))
      modifiedConfig.SendMail_mailling.mails_surfacing = [
        ...newProperties.mod_settings.SendMail_mailling.mails_surfacing,
      ]
    if (newProperties.mod_settings?.SendMail_mailling?.hasOwnProperty('mails_detection'))
      modifiedConfig.SendMail_mailling.mails_detection = [
        ...newProperties.mod_settings.SendMail_mailling.mails_detection,
      ]
    //Vue.set(state.modifiedSendMailConfigs,itemId,merge(state.modifiedSendMailConfigs[itemId],newProperties))
  },
  [types.MISSION_CANCEL_MODIFIED_SEND_MAIL]: (state, itemId: number) => {
    const cloneConfig = new IrisConfigContainer(JSON.parse(JSON.stringify(state.originalSendMailConfigs[itemId])))
    Vue.set(state.modifiedSendMailConfigs, itemId, cloneConfig)
  },
  [types.MISSION_IRIS_SEND_MAIL_SAVE_REQUEST]: state => {
    state.status = 'saving send mail config'
    state.sendMailLoading = true
  },
  [types.MISSION_IRIS_SEND_MAIL_SAVE_SUCCESS]: (
    state,
    { id, sendMailConfig }: { id: number; sendMailConfig: IrisConfigContainer<ISendMailConfig> },
  ) => {
    state.status = 'success saving send mail config'
    normalizeSendMailArrays(sendMailConfig)
    const cloneConfig = new IrisConfigContainer(JSON.parse(JSON.stringify(sendMailConfig)))
    Vue.set(state.modifiedSendMailConfigs, id, cloneConfig)
    Vue.set(state.originalSendMailConfigs, id, sendMailConfig)
    state.sendMailLoading = false
  },

  [types.MISSION_IRIS_SEND_MAIL_SAVE_ERROR]: state => {
    state.status = 'error saving send mail config'
    state.sendMailLoading = false
  },
  [types.MISSION_LBK_ALERT_SAVE_REQUEST]: state => {
    state.status = 'saving lbk alert mission'
    //Rien a voir avec le lbk ALERT
    //state.missionsPassedLoaded = true
  },
  [types.MISSION_LBK_ALERT_SAVE_ERROR]: state => {
    state.status = 'error saving lbk alert mission'
    //Rien a voir avec le lbk ALERT
    //state.missionsPassedLoaded = false
  },
  [types.MISSION_LBK_ALERT_SAVE_SUCCESS]: (state, missionId: number) => {
    state.status = `success save LBK_ALERT for mission ${missionId}`
    //Rien a voir avec le lbk ALERT
    //state.missionsPassedLoaded = false
  },

  [types.MISSION_PLAN_REQUEST]: (state, missionId: number) => {
    state.status = `Request mission plan for mission ${missionId}`
    state.missionPlanStagingLoading = true
  },
  [types.MISSION_PLAN_PUBLISH_REQUEST]: (state, mpsId: number) => {
    state.status = `publish staging mission plan ${mpsId} to iris`
    state.missionPlanStagingLoading = true
  },
  [types.MISSION_PLAN_SAVE_REQUEST]: (state, missionPlan: AloMissionPlan) => {
    state.status = 'saving mission plan'
    state.missionPlanStagingLoading = true
  },
  [types.MISSION_PLAN_POPULATE_FROM_TEMPLATE_REQUEST]: (state, { misId, mptId }: { misId: number; mptId: number }) => {
    state.status = `populate last mission plan with template ${mptId} for mission ${misId}`
    state.missionPlanStagingLoading = true
  },

  [types.MISSION_PLAN_SUCCESS]: (state, { misId, missionPlan }: { misId: number; missionPlan: AloMissionPlan }) => {
    state.status = `success request mission plan for mission ${misId}`
    //Mise à jour des données file status
    const index = state.missions.findIndex(m => m.misId == misId)
    if (index > -1) {
      const mission = state.missions[index]
      mission._missionPlanLoaded = true
      mission.missionPlanStaging = missionPlan
    }
    state.missionPlanStagingLoading = false
  },
  [types.MISSION_PLAN_PUBLISH_SUCCESS]: (state, missionId: number) => {
    state.status = `success publish staging mission plan to iris for mission ${missionId}`
    state.missionPlanStagingLoading = false
  },
  [types.MISSION_PLAN_SAVE_SUCCESS]: (state, missionPlan: AloMissionPlan) => {
    state.status = `success saving mission plan for mission ${missionPlan.misId}`
    //Mise à jour des données file status
    const index = state.missions.findIndex(m => m.misId == missionPlan.misId)
    if (index > -1) {
      const mission = state.missions[index]
      mission._missionPlanLoaded = true
      mission.missionPlanStaging = missionPlan
    }
    state.missionPlanStagingLoading = false
  },
  [types.MISSION_PLAN_POPULATE_FROM_TEMPLATE_SUCCESS]: (state, missionPlan: AloMissionPlan) => {
    state.status = `success populate last mission plan with template ${missionPlan.mptId} for mission ${missionPlan.misId}`
    //Mise à jour des données file status
    const index = state.missions.findIndex(m => m.misId == missionPlan.misId)
    if (index > -1) {
      const mission = state.missions[index]
      mission._missionPlanLoaded = true
      mission.missionPlanStaging = missionPlan
    }
    state.missionPlanStagingLoading = false
  },

  [types.MISSION_PLAN_ERROR]: (state, missionId: number) => {
    state.status = `error Request mission plan for mission ${missionId}`
    state.missionPlanStagingLoading = false
  },
  [types.MISSION_PLAN_PUBLISH_ERROR]: (state, missionId: number) => {
    state.status = `error publish staging mission plan to iris for mission ${missionId}`
    state.missionPlanStagingLoading = false
  },
  [types.MISSION_PLAN_SAVE_ERROR]: (state, missionPlan: AloMissionPlan) => {
    state.status = 'error saving mission plan'
    state.missionPlanStagingLoading = false
  },
  [types.MISSION_PLAN_POPULATE_FROM_TEMPLATE_ERROR]: (state, { misId, mptId }: { misId: number; mptId: number }) => {
    state.status = `error populate last mission plan with template ${mptId} for mission ${misId}`
    state.missionPlanStagingLoading = false
  },
  [types.MISSION_IRIS_MISSION_PLAN_REQUEST]: state => {
    state.status = 'Get current mission plan'
    state.missionPlanIrisLoading = true
  },
  [types.MISSION_IRIS_MISSION_PLAN_SUCCESS]: (
    state,
    { id, missionPlanState }: { id: number; missionPlanState: IMissionPlan },
  ) => {
    state.status = 'success getting current mission plan'
    //si les config sont identique on met à jour les deux
    Vue.set(state.originalIrisMissionPlan, id, missionPlanState)
    state.missionPlanIrisLoading = false
  },
  [types.MISSION_IRIS_MISSION_PLAN_ERROR]: state => {
    state.status = 'error getting current mission plan'
    state.missionPlanIrisLoading = false
  },
  [types.MISSION_IRIS_MISSION_PLAN_CONFIG_REQUEST]: state => {
    state.status = 'loading mission plan config'
    state.missionPlanConfigLoading = true
  },
  [types.MISSION_IRIS_MISSION_PLAN_CONFIG_SUCCESS]: (
    state,
    { id, missionPlanConfig }: { id: number; missionPlanConfig: IrisConfigContainer<IMissionPlanConfig> },
  ) => {
    state.status = 'success loading mission plan config'

    //si les config sont identique on met à jour les deux
    if (deepEqual(state.originalMissionPlanConfigs[id], state.originalMissionPlanConfigs[id])) {
      const cloneConfig = new IrisConfigContainer(JSON.parse(JSON.stringify(missionPlanConfig)))
      Vue.set(state.modifiedMissionPlanConfigs, id, cloneConfig)
    }
    Vue.set(state.originalMissionPlanConfigs, id, missionPlanConfig)
    state.missionPlanConfigLoading = false
  },
  [types.MISSION_IRIS_MISSION_PLAN_CONFIG_ERROR]: state => {
    state.status = 'error loading mission plan config'
    state.missionPlanConfigLoading = false
  },
  [types.MISSION_UPDATE_MODIFIED_MISSION_PLAN_CONFIG]: (
    state,
    { itemId, newProperties }: { itemId: number; newProperties: any },
  ) => {
    const modifiedConfig = state.modifiedMissionPlanConfigs[itemId].mod_settings
    if (newProperties.mod_settings?.hasOwnProperty('EnabledMOD'))
      modifiedConfig.EnabledMOD = newProperties.mod_settings.EnabledMOD
    if (!modifiedConfig.MissionPlan_current_correction)
      modifiedConfig.MissionPlan_current_correction = { ...newProperties.mod_settings.MissionPlan_current_correction }
    else {
      if (newProperties.mod_settings?.MissionPlan_current_correction?.hasOwnProperty('enable'))
        modifiedConfig.MissionPlan_current_correction.enable = Boolean(
          newProperties.mod_settings.MissionPlan_current_correction.enable,
        )
    }
    if (!modifiedConfig.MissionPlan_payload)
      modifiedConfig.MissionPlan_payload = { ...newProperties.mod_settings.MissionPlan_payload }
    else {
      if (newProperties.mod_settings?.MissionPlan_payload?.hasOwnProperty('enable'))
        modifiedConfig.MissionPlan_payload.enable = Boolean(newProperties.mod_settings.MissionPlan_payload.enable)
    }
    if (!modifiedConfig.MissionPlan) modifiedConfig.MissionPlan = { ...newProperties.mod_settings.MissionPlan }
    else {
      if (newProperties.mod_settings?.MissionPlan?.hasOwnProperty('current_mode'))
        modifiedConfig.MissionPlan.current_mode = newProperties.mod_settings.MissionPlan.current_mode
      if (newProperties.mod_settings?.MissionPlan?.hasOwnProperty('lookahead'))
        modifiedConfig.MissionPlan.lookahead = newProperties.mod_settings.MissionPlan.lookahead
    }

    /*if(!modifiedConfig.MissionPlan_drift_corrected) modifiedConfig.MissionPlan_drift_corrected = {...newProperties.mod_settings.MissionPlan_drift_corrected}
      else {
        if(newProperties.mod_settings.MissionPlan_drift_corrected.hasOwnProperty('enable')) modifiedConfig.MissionPlan_drift_corrected.enable = Boolean(newProperties.mod_settings.MissionPlan_drift_corrected.enable)
        if(newProperties.mod_settings.MissionPlan_drift_corrected.hasOwnProperty('factor')) modifiedConfig.MissionPlan_drift_corrected.factor = Number(newProperties.mod_settings.MissionPlan_drift_corrected.factor)      
      }*/
    //Vue.set(state.SingleWayPoint_target,itemId,merge(state.modifiedNsdConfigs[itemId],newProperties))
  },
  [types.MISSION_CANCEL_MODIFIED_MISSION_PLAN_CONFIG]: (state, itemId: number) => {
    const cloneConfig = new IrisConfigContainer(JSON.parse(JSON.stringify(state.originalMissionPlanConfigs[itemId])))
    Vue.set(state.modifiedMissionPlanConfigs, itemId, cloneConfig)
  },
  [types.MISSION_IRIS_MISSION_PLAN_CONFIG_SAVE_REQUEST]: state => {
    state.status = 'saving mission plan config'
    state.missionPlanConfigLoading = true
  },
  [types.MISSION_IRIS_MISSION_PLAN_CONFIG_SAVE_SUCCESS]: (
    state,
    { id, missionPlanConfig }: { id: number; missionPlanConfig: IrisConfigContainer<IMissionPlanConfig> },
  ) => {
    state.status = 'success saving single way point config'
    const cloneConfig = new IrisConfigContainer(JSON.parse(JSON.stringify(missionPlanConfig)))
    Vue.set(state.modifiedMissionPlanConfigs, id, cloneConfig)
    Vue.set(state.originalMissionPlanConfigs, id, missionPlanConfig)
    state.missionPlanConfigLoading = false
  },
  [types.MISSION_IRIS_MISSION_PLAN_CONFIG_SAVE_ERROR]: state => {
    state.status = 'error saving mission plan config'
    state.missionPlanConfigLoading = false
  },
  [types.MISSION_IRIS_AD2CP_REQUEST]: state => {
    state.status = 'loading ad2cp config'
    state.ad2cpLoading = true
  },
  [types.MISSION_IRIS_AD2CP_SUCCESS]: (
    state,
    { id, ad2cpConfig }: { id: number; ad2cpConfig: IrisConfigContainer<IAd2cpConfig> },
  ) => {
    state.status = 'success loading ad2cp config'

    //si les config sont identique on met à jour les deux
    if (deepEqual(state.originalAd2cpConfigs[id], state.modifiedAd2cpConfigs[id])) {
      const cloneConfig = new IrisConfigContainer(JSON.parse(JSON.stringify(ad2cpConfig)))
      Vue.set(state.modifiedAd2cpConfigs, id, cloneConfig)
    }
    Vue.set(state.originalAd2cpConfigs, id, ad2cpConfig)
    state.ad2cpLoading = false
  },
  [types.MISSION_IRIS_AD2CP_ERROR]: state => {
    state.status = 'error loading ad2cp config'
    state.ad2cpLoading = false
  },
  [types.MISSION_UPDATE_MODIFIED_AD2CP]: (state, { itemId, newProperties }: { itemId: number; newProperties: any }) => {
    const modifiedConfig = state.modifiedAd2cpConfigs[itemId].mod_settings
    if (newProperties.mod_settings?.hasOwnProperty('EnabledMOD'))
      modifiedConfig.EnabledMOD = newProperties.mod_settings.EnabledMOD
    if (newProperties.mod_settings?.hasOwnProperty('AD2CP_QC'))
      modifiedConfig.AD2CP_QC = { ...modifiedConfig.AD2CP_QC, ...newProperties.mod_settings.AD2CP_QC }
    if (newProperties.mod_settings?.hasOwnProperty('AD2CP_Sensor'))
      modifiedConfig.AD2CP_Sensor = { ...modifiedConfig.AD2CP_Sensor, ...newProperties.mod_settings.AD2CP_Sensor }
  },
  [types.MISSION_CANCEL_MODIFIED_AD2CP]: (state, itemId: number) => {
    const cloneConfig = new IrisConfigContainer(JSON.parse(JSON.stringify(state.originalAd2cpConfigs[itemId])))
    Vue.set(state.modifiedAd2cpConfigs, itemId, cloneConfig)
  },
  [types.MISSION_IRIS_AD2CP_SAVE_REQUEST]: state => {
    state.status = 'saving ad2cp config'
    state.ad2cpLoading = true
  },
  [types.MISSION_IRIS_AD2CP_SAVE_SUCCESS]: (
    state,
    { id, ad2cpConfig }: { id: number; ad2cpConfig: IrisConfigContainer<IAd2cpConfig> },
  ) => {
    state.status = 'success saving ad2cp config'
    const cloneConfig = new IrisConfigContainer(JSON.parse(JSON.stringify(ad2cpConfig)))
    Vue.set(state.modifiedAd2cpConfigs, id, cloneConfig)
    Vue.set(state.originalAd2cpConfigs, id, ad2cpConfig)
    state.ad2cpLoading = false
  },
  [types.MISSION_IRIS_AD2CP_SAVE_ERROR]: state => {
    state.status = 'error saving ad2cp config'
    state.ad2cpLoading = false
  },
  [types.MISSION_IRIS_AD2CP_RUN_REQUEST]: state => {
    state.status = 'runing ad2cp recalcul'
    state.ad2cpLoading = true
  },
  [types.MISSION_IRIS_AD2CP_RUN_SUCCESS]: state => {
    state.status = 'success runing ad2cp reprocessing'
    state.ad2cpLoading = false
  },
  [types.MISSION_IRIS_AD2CP_RUN_ERROR]: state => {
    state.status = 'error runing ad2cp recalcul'
    state.ad2cpLoading = false
  },
  [types.MISSION_IRIS_CORIOLIS_REQUEST]: state => {
    state.status = 'loading coriolis config'
    state.coriolisLoading = true
  },
  [types.MISSION_IRIS_CORIOLIS_SUCCESS]: (
    state,
    { id, coriolisConfig }: { id: number; coriolisConfig: IrisConfigContainer<ICoriolisConfig> },
  ) => {
    state.status = 'success loading coriolis config'

    //si les config sont identique on met à jour les deux
    const cloneConfig = new IrisConfigContainer(JSON.parse(JSON.stringify(coriolisConfig)))
    Vue.set(state.modifiedCoriolisConfigs, id, cloneConfig)
    Vue.set(state.originalCoriolisConfigs, id, coriolisConfig)
    state.coriolisLoading = false
  },
  [types.MISSION_IRIS_CORIOLIS_ERROR]: state => {
    state.status = 'error loading coriolis config'
    state.coriolisLoading = false
  },
  [types.MISSION_UPDATE_MODIFIED_CORIOLIS]: (
    state,
    { itemId, newProperties }: { itemId: number; newProperties: any },
  ) => {
    const modifiedConfig = state.modifiedCoriolisConfigs[itemId].mod_settings
    if (newProperties.mod_settings?.hasOwnProperty('EnabledMOD'))
      modifiedConfig.EnabledMOD = newProperties.mod_settings.EnabledMOD
    if (newProperties.mod_settings?.hasOwnProperty('Coriolis_Export'))
      modifiedConfig.Coriolis_Export = newProperties.mod_settings.Coriolis_Export
  },
  [types.MISSION_CANCEL_MODIFIED_CORIOLIS]: (state, itemId: number) => {
    const cloneConfig = new IrisConfigContainer(JSON.parse(JSON.stringify(state.originalCoriolisConfigs[itemId])))
    Vue.set(state.modifiedCoriolisConfigs, itemId, cloneConfig)
  },
  [types.MISSION_IRIS_CORIOLIS_SAVE_REQUEST]: state => {
    state.status = 'saving coriolis config'
    state.coriolisLoading = true
  },
  [types.MISSION_IRIS_CORIOLIS_SAVE_SUCCESS]: (
    state,
    { id, coriolisConfig }: { id: number; coriolisConfig: IrisConfigContainer<ICoriolisConfig> },
  ) => {
    state.status = 'success saving coriolis config'
    const cloneConfig = new IrisConfigContainer(JSON.parse(JSON.stringify(coriolisConfig)))
    Vue.set(state.modifiedCoriolisConfigs, id, cloneConfig)
    Vue.set(state.originalCoriolisConfigs, id, coriolisConfig)
    state.coriolisLoading = false
  },
  [types.MISSION_IRIS_CORIOLIS_SAVE_ERROR]: state => {
    state.status = 'error saving coriolis config'
    state.coriolisLoading = false
  },
  [types.MISSION_IRIS_CORIOLIS_RUN_REQUEST]: state => {
    state.status = 'runing coriolis export'
    state.coriolisLoading = true
  },
  [types.MISSION_IRIS_CORIOLIS_RUN_SUCCESS]: state => {
    state.status = 'success runing coriolis export'
    state.coriolisLoading = false
  },
  [types.MISSION_IRIS_CORIOLIS_RUN_ERROR]: state => {
    state.status = 'error runing coriolis export'
    state.coriolisLoading = false
  },
  [types.MISSION_IRIS_AIS_REQUEST]: state => {
    state.status = 'loading ais config'
    state.aisLoading = true
  },
  [types.MISSION_IRIS_AIS_SUCCESS]: (
    state,
    { id, aisConfig }: { id: number; aisConfig: IrisConfigContainer<IAisConfig> },
  ) => {
    state.status = 'success loading ais config'

    //si les config sont identique on met à jour les deux
    if (deepEqual(state.originalAisConfigs[id], state.modifiedAisConfigs[id])) {
      const cloneConfig = new IrisConfigContainer(JSON.parse(JSON.stringify(aisConfig)))
      Vue.set(state.modifiedAisConfigs, id, cloneConfig)
    }
    Vue.set(state.originalAisConfigs, id, aisConfig)
    state.aisLoading = false
  },
  [types.MISSION_IRIS_AIS_ERROR]: state => {
    state.status = 'error loading ais config'
    state.aisLoading = false
  },
  [types.MISSION_UPDATE_MODIFIED_AIS]: (state, { itemId, newProperties }: { itemId: number; newProperties: any }) => {
    const modifiedConfig = state.modifiedAisConfigs[itemId].mod_settings
    if (newProperties.mod_settings?.hasOwnProperty('EnabledMOD'))
      modifiedConfig.EnabledMOD = newProperties.mod_settings.EnabledMOD
    if (newProperties.mod_settings?.hasOwnProperty('AIS'))
      modifiedConfig.AIS = { ...modifiedConfig.AIS, ...newProperties.mod_settings.AIS }
  },
  [types.MISSION_CANCEL_MODIFIED_AIS]: (state, itemId: number) => {
    const cloneConfig = new IrisConfigContainer(JSON.parse(JSON.stringify(state.originalAisConfigs[itemId])))
    Vue.set(state.modifiedAisConfigs, itemId, cloneConfig)
  },
  [types.MISSION_IRIS_AIS_SAVE_REQUEST]: state => {
    state.status = 'saving ais config'
    state.aisLoading = true
  },
  [types.MISSION_IRIS_AIS_SAVE_SUCCESS]: (
    state,
    { id, aisConfig }: { id: number; aisConfig: IrisConfigContainer<IAisConfig> },
  ) => {
    state.status = 'success saving ais config'
    const cloneConfig = new IrisConfigContainer(JSON.parse(JSON.stringify(aisConfig)))
    Vue.set(state.modifiedAisConfigs, id, cloneConfig)
    Vue.set(state.originalAisConfigs, id, aisConfig)
    state.aisLoading = false
  },
  [types.MISSION_IRIS_AIS_SAVE_ERROR]: state => {
    state.status = 'error saving ais config'
    state.aisLoading = false
  },
  [types.MISSION_IRIS_AIS_STATUS_UPDATE]: (state, { id, module_status }: { id: number; module_status: string }) => {
    if (state.originalAisConfigs[id]) {
      Vue.set(state.originalAisConfigs[id].mod_settings.ANTICOLL, 'module_status', module_status)
    }
    if (state.modifiedAisConfigs[id]) {
      Vue.set(state.modifiedAisConfigs[id].mod_settings.ANTICOLL, 'module_status', module_status)
    }
    const index = state.missions.findIndex(m => m.misId == id)
    if (index > -1) {
      const mission = state.missions[index]
      mission.aisStatus = module_status as 'OFF' | 'NO_RISK' | 'RISK' | 'NO_DATA'
    }
  },
  [types.MISSION_IRIS_ENCRYPTION_GET_REQUEST]: state => {
    state.encryptionRevealLoading = true
  },
  [types.MISSION_IRIS_ENCRYPTION_GET_SUCCESS]: state => {
    state.encryptionRevealLoading = false
  },
  [types.MISSION_IRIS_ENCRYPTION_GET_ERROR]: state => {
    state.encryptionRevealLoading = false
  },
  [types.MISSION_IRIS_ENCRYPTION_SAVE_REQUEST]: state => {
    state.encryptionSaveLoading = true
  },
  [types.MISSION_IRIS_ENCRYPTION_SAVE_SUCCESS]: state => {
    state.encryptionSaveLoading = false
  },
  [types.MISSION_IRIS_ENCRYPTION_SAVE_ERROR]: state => {
    state.encryptionSaveLoading = false
  },
  [types.MISSION_RUN_END_SIMULATION_REQUEST]: (state, id: number) => {
    state.status = `run end mission simulation ${id}`
    state.simulationRunning = true
  },
  [types.MISSION_RUN_END_SIMULATION_SUCCESS]: (
    state,
    { misId, smartMission }: { misId: number; smartMission: ISmartMission },
  ) => {
    state.status = `success end mission simulation ${misId}`
    state.simulationRunning = false

    const index = state.missions.findIndex(m => m.misId == misId)
    if (index > -1) {
      const mission = state.missions[index]
      //on memorise les parametre sur la mission
      mission.param = smartMission
      mission.vimIteration = smartMission.simulation_iteration
      mission.vimStatus = SimulationStatus.running
      if (mission.simulation) mission.simulation._detailLoaded = false
    }
  },
  [types.MISSION_RUN_END_SIMULATION_ERROR]: state => {
    state.status = 'error end mission simulation'
    state.simulationRunning = false
  },
  [types.MISSION_ENDMISSION_UPDATE_PROGRESSION]: (
    state,
    { mission, wob }: { mission: AloMission; wob: WobSimProgress },
  ) => {
    if (mission) {
      mission.vimResultJson = wob.irisRawResult
      mission.result = { ...getDefaultSmartResult(), ...(wob.irisResult ?? {}) }
      mission.hasEndMissionSimulation = true
      mission.vimIteration = wob.simulationIteration
      //fausse date d'execution pour forcer le refresh si simu not applicable affiché
      mission.vimDatexec = new Date()

      //@ts-ignore
      mission.simulation = AloMission.buildSimulation(mission, true)
      mission.vimStatus = getSimulationStatus(wob.simulationState)
      mission.simulation!._detailLoaded = (mission.vimStatus ?? 0) < 0 || mission.vimStatus == 10

      // if(wob.irisResult == null)  {

      // }
    }
  },

  [types.MISSION_IRIS_QUICKCONTROL_CONFIG_REQUEST]: state => {
    state.status = 'loading quickcontrol config'
    state.quickcontrolConfigLoading = true
  },
  [types.MISSION_IRIS_QUICKCONTROL_CONFIG_SUCCESS]: (
    state,
    { id, config }: { id: number; config: IrisConfigContainer<IQuickControlConfig> },
  ) => {
    state.status = 'success loading quickcontrol config'
    if (deepEqual(state.originalQuickControlConfigs[id], state.modifiedQuickControlConfigs[id])) {
      Vue.set(state.modifiedQuickControlConfigs, id, new IrisConfigContainer(JSON.parse(JSON.stringify(config))))
    }
    Vue.set(state.originalQuickControlConfigs, id, config)
    state.quickcontrolConfigLoading = false
  },
  [types.MISSION_IRIS_QUICKCONTROL_CONFIG_ERROR]: state => {
    state.status = 'error loading quickcontrol config'
    state.quickcontrolConfigLoading = false
  },

  [types.MISSION_IRIS_QUICKCONTROL_CONFIG_SAVE_REQUEST]: state => {
    state.status = 'saving quickcontrol config'
    state.quickcontrolConfigLoading = true
  },
  [types.MISSION_IRIS_QUICKCONTROL_CONFIG_SAVE_SUCCESS]: (
    state,
    { id, config }: { id: number; config: IrisConfigContainer<IQuickControlConfig> },
  ) => {
    state.status = 'success saving quickcontrol config'
    Vue.set(state.modifiedQuickControlConfigs, id, new IrisConfigContainer(JSON.parse(JSON.stringify(config))))
    Vue.set(state.originalQuickControlConfigs, id, config)
    state.quickcontrolConfigLoading = false
  },
  [types.MISSION_IRIS_QUICKCONTROL_CONFIG_SAVE_ERROR]: state => {
    state.status = 'error saving quickcontrol config'
    state.quickcontrolConfigLoading = false
  },

  [types.MISSION_UPDATE_MODIFIED_QUICKCONTROL_CONFIG]: (
    state,
    { itemId, config }: { itemId: number; config: IrisConfigContainer<IQuickControlConfig> },
  ) => {
    const next = new IrisConfigContainer(JSON.parse(JSON.stringify(config)))
    // Dédup contenu : éviter de recréer une référence identique. Sans ce garde,
    // l'écho du panneau EasyWaypoint (initFromConfig → localNavProfile neuf →
    // watcher deep → pushModified) boucle à l'infini sur tout update externe
    // (map « set target here » / drag du point). Cf. mutation SUCCESS ci-dessus.
    if (deepEqual(state.modifiedQuickControlConfigs[itemId], next)) return
    Vue.set(state.modifiedQuickControlConfigs, itemId, next)
  },
  [types.MISSION_CANCEL_MODIFIED_QUICKCONTROL]: (state, itemId: number) => {
    Vue.set(
      state.modifiedQuickControlConfigs,
      itemId,
      new IrisConfigContainer(JSON.parse(JSON.stringify(state.originalQuickControlConfigs[itemId]))),
    )
  },
}

export default mutations
